import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "../context/I18nContext";
import { getAvatarUrl, getInitials } from "../utils/avatar";
import * as teachersPublicService from "../services/teachersPublic";
import "./TeacherPublicProfile.css";

function Avatar({ user, className }) {
  const { t } = useTranslation();
  const url = getAvatarUrl(user);
  const initials = useMemo(() => getInitials(user), [user]);
  const fallbackLabel = t("teacherPublicProfile.avatarAlt");

  if (url) {
    return (
      <img
        className={className}
        src={url}
        alt={user?.full_name || fallbackLabel}
        loading="lazy"
      />
    );
  }

  return (
    <div className={className} aria-label={user?.full_name || fallbackLabel}>
      <span>{initials}</span>
    </div>
  );
}

export default function TeacherPublicProfile() {
  const { teacherId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [teacher, setTeacher] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let ignore = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [detail, list] = await Promise.all([
          teachersPublicService.fetchPublicTeacher({ teacherId }),
          teachersPublicService.fetchPublicTeachers(),
        ]);

        if (ignore) return;
        setTeacher(detail.teacher || null);

        const others = (list.teachers || []).filter(
          (t) => String(t.id) !== String(teacherId),
        );
        setTeachers(others);
      } catch (e) {
        if (!ignore) {
          setError(e.message || "");
          setTeacher(null);
          setTeachers([]);
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    load();
    return () => {
      ignore = true;
    };
  }, [teacherId]);

  const headline = useMemo(() => {
    if (!teacher) return "";
    if (teacher?.headline) return teacher.headline;
    return t("teacherPublicProfile.labels.defaultHeadline");
  }, [t, teacher]);

  const locationLabel = useMemo(() => {
    if (!teacher) return "";
    return teacher?.location || "";
  }, [teacher]);

  return (
    <div className="teacher-public-page">
      <div className="teacher-public-shell">
        <div className="teacher-public-top">
          <button
            type="button"
            className="teacher-public-back"
            onClick={() => navigate(-1)}
          >
            {t("teacherPublicProfile.actions.back")}
          </button>
        </div>

        {loading ? (
          <div className="teacher-public-loading">
            {t("teacherPublicProfile.states.loading")}
          </div>
        ) : error ? (
          <div className="teacher-public-error">
            {error || t("teacherPublicProfile.states.loadError")}
          </div>
        ) : !teacher ? (
          <div className="teacher-public-error">
            {t("teacherPublicProfile.states.notFound")}
          </div>
        ) : (
          <div className="teacher-public-layout">
            <main className="teacher-public-main">
              <section className="teacher-public-card">
                <div className="teacher-public-cover" />

                <div className="teacher-public-head">
                  <div className="teacher-public-avatarWrap">
                    <Avatar user={teacher} className="teacher-public-avatar" />
                  </div>

                  <div className="teacher-public-identity">
                    <h1 className="teacher-public-name">{teacher.full_name}</h1>
                    <div className="teacher-public-headline">{headline}</div>
                    <div className="teacher-public-meta">
                      {locationLabel ? (
                        <span className="teacher-public-metaItem">
                          {locationLabel}
                        </span>
                      ) : null}
                      {teacher?.joined_at ? (
                        <span className="teacher-public-metaItem">
                          {t("teacherPublicProfile.labels.since")}{" "}
                          {new Date(teacher.joined_at).getFullYear()}
                        </span>
                      ) : null}
                      {teacher?.is_verified ? (
                        <span className="teacher-public-verified">
                          {t("teacherPublicProfile.labels.verified")}
                        </span>
                      ) : null}
                    </div>
                  </div>

                  <div className="teacher-public-actions">
                    <button
                      type="button"
                      className="btn outline teacher-public-actionBtn"
                      disabled
                      aria-disabled="true"
                      title={t("teacherPublicProfile.tooltips.comingSoon")}
                    >
                      {t("teacherPublicProfile.actions.follow")}
                    </button>
                    <button
                      type="button"
                      className="btn primary teacher-public-actionBtn"
                      disabled
                      aria-disabled="true"
                      title={t("teacherPublicProfile.tooltips.comingSoon")}
                    >
                      {t("teacherPublicProfile.actions.chat")}
                    </button>
                  </div>
                </div>
              </section>

              <section className="teacher-public-section">
                <h2 className="teacher-public-sectionTitle">
                  {t("teacherPublicProfile.sections.about")}
                </h2>
                <p className="teacher-public-sectionBody">
                  {teacher?.about || t("teacherPublicProfile.labels.noInfo")}
                </p>
              </section>

              <section className="teacher-public-section">
                <h2 className="teacher-public-sectionTitle">
                  {t("teacherPublicProfile.sections.experience")}
                </h2>
                <div className="teacher-public-sectionBody teacher-public-list">
                  {Array.isArray(teacher?.experience) &&
                  teacher.experience.length
                    ? teacher.experience.map((item, idx) => (
                        <div className="teacher-public-item" key={idx}>
                          <div className="teacher-public-itemTitle">
                            {item.title || "—"}
                          </div>
                          <div className="teacher-public-itemMeta">
                            {item.org || ""}
                            {item.location ? ` • ${item.location}` : ""}
                            {item.dates ? ` • ${item.dates}` : ""}
                          </div>
                        </div>
                      ))
                    : t("teacherPublicProfile.labels.noInfo")}
                </div>
              </section>

              <section className="teacher-public-section">
                <h2 className="teacher-public-sectionTitle">
                  {t("teacherPublicProfile.sections.education")}
                </h2>
                <div className="teacher-public-sectionBody teacher-public-list">
                  {Array.isArray(teacher?.education) && teacher.education.length
                    ? teacher.education.map((item, idx) => (
                        <div className="teacher-public-item" key={idx}>
                          <div className="teacher-public-itemTitle">
                            {item.school || "—"}
                          </div>
                          <div className="teacher-public-itemMeta">
                            {item.degree || ""}
                            {item.dates ? ` • ${item.dates}` : ""}
                          </div>
                        </div>
                      ))
                    : t("teacherPublicProfile.labels.noInfo")}
                </div>
              </section>

              <section className="teacher-public-section">
                <h2 className="teacher-public-sectionTitle">
                  {t("teacherPublicProfile.sections.skills")}
                </h2>
                <div className="teacher-public-skills">
                  {Array.isArray(teacher?.skills) && teacher.skills.length
                    ? teacher.skills.map((skill, idx) => (
                        <span className="teacher-public-skill" key={idx}>
                          {skill}
                        </span>
                      ))
                    : t("teacherPublicProfile.labels.noInfo")}
                </div>
              </section>
            </main>

            <aside className="teacher-public-side">
              <div className="teacher-public-sideCard">
                <div className="teacher-public-sideTitle">
                  {t("teacherPublicProfile.sections.teachers")}
                </div>
                <div className="teacher-public-sideList">
                  {(teachers || []).slice(0, 6).map((t) => (
                    <div className="teacher-public-sideRow" key={t.id}>
                      <Avatar user={t} className="teacher-public-sideAvatar" />
                      <div className="teacher-public-sideInfo">
                        <div className="teacher-public-sideName">
                          {t.full_name}
                        </div>
                        <div className="teacher-public-sideSub">
                          {t("teacherPublicProfile.labels.teacher")}
                        </div>
                      </div>
                      <Link
                        to={`/teachers/${t.id}`}
                        className="btn outline teacher-public-sideBtn"
                      >
                        {t("teacherPublicProfile.actions.viewProfile")}
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        )}
      </div>
    </div>
  );
}
